import axios from "axios";
import { logsApi } from "@/api/logs";

const publicAxios = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || "http://localhost:5000/api",
  timeout: 5000,
});

interface VisitData {
  path: string;
  pageName?: string;
  referrer?: string;
  source?: string;
  duration?: number;
  pageLoadTime?: number;
}

class VisitTracker {
  private visitorId: string;
  private startTime: number = 0;
  private currentPath: string = "";
  private isTracked: boolean = false;

  constructor() {
    this.visitorId = this.getOrCreateVisitorId();
    this.initNavigationTiming();
  }

  private getOrCreateVisitorId(): string {
    let id = localStorage.getItem("mier_visitor_id");
    if (!id) {
      id = this.generateId();
      localStorage.setItem("mier_visitor_id", id);
    }
    return id;
  }

  private generateId(): string {
    return (
      "v_" + Date.now().toString(36) + Math.random().toString(36).substr(2, 9)
    );
  }

  private initNavigationTiming(): void {
    window.addEventListener("load", () => {
      const timing = performance.timing;
      let loadTime = 0;
      let ttfb = 0;

      if (timing.loadEventEnd > 0 && timing.navigationStart > 0) {
        loadTime = Math.max(0, timing.loadEventEnd - timing.navigationStart);
      }

      if (timing.responseStart > 0 && timing.requestStart > 0) {
        ttfb = timing.responseStart - timing.requestStart;
      }

      this.sendVisit({
        path: window.location.pathname,
        pageName: document.title,
        referrer: document.referrer,
        pageLoadTime: loadTime > 0 ? Math.round(loadTime) : undefined,
      });
    });
  }

  trackPageView(path: string, pageName?: string): void {
    this.currentPath = path;
    this.startTime = Date.now();
    this.isTracked = false;

    const navigationEntries = performance.getEntriesByType("navigation");
    let pageLoadTime: number | undefined;
    if (navigationEntries.length > 0) {
      const nav = navigationEntries[0] as PerformanceNavigationTiming;
      if (nav.domContentLoadedEventEnd > 0 && nav.startTime > 0) {
        const calculated = nav.domContentLoadedEventEnd - nav.startTime;
        pageLoadTime = calculated > 0 ? Math.round(calculated) : undefined;
      }
    }

    this.sendVisit({
      path,
      pageName,
      referrer: document.referrer,
      pageLoadTime,
    });
  }

  trackDuration(): void {
    if (!this.currentPath || this.isTracked) return;

    const duration = Math.round((Date.now() - this.startTime) / 1000);
    if (duration < 1) return;

    this.sendVisit({
      path: this.currentPath,
      duration,
    });
    this.isTracked = true;
  }

  private async sendVisit(data: VisitData): Promise<void> {
    try {
      await publicAxios.post("/public/logs/visit", {
        type: "PAGE",
        path: data.path,
        pageName: data.pageName,
        referrer: data.referrer,
        source: this.getSource(),
        visitorId: this.visitorId,
        duration: data.duration,
        pageLoadTime: data.pageLoadTime,
      });
    } catch (error) {}
  }

  private getSource(): string {
    const referrer = document.referrer;
    if (!referrer) return "DIRECT";
    if (referrer.includes("baidu.com")) return "BAIDU";
    if (referrer.includes("google.com")) return "GOOGLE";
    if (referrer.includes("bing.com")) return "BING";
    if (referrer.includes("so.com")) return "SO";
    if (referrer.includes("sogou.com")) return "SOGOU";
    if (referrer.includes("weixin.qq.com")) return "WEIXIN";
    if (referrer.includes("m.weibo.cn")) return "WEIBO";
    if (referrer.includes("facebook.com")) return "FACEBOOK";
    if (referrer.includes("twitter.com")) return "TWITTER";
    return "OTHER";
  }

  getVisitorId(): string {
    return this.visitorId;
  }
}

export const visitTracker = new VisitTracker();
